import { useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { Page } from '@src/components';
import creativeStudioApi from '@src/api/creativeStudio';
import { useMessage } from '@src/context/common';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const TAB_OPTIONS = [
  { value: 'hooks', label: 'Hooks' },
  { value: 'assets', label: 'Assets' },
  { value: 'variants', label: 'Ad Variants' },
  { value: 'jobs', label: 'Render Jobs' },
];

const VERTICAL_OPTIONS = [
  { value: 'bath', label: 'Bath' },
  { value: 'windows', label: 'Windows' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'siding', label: 'Siding' },
  { value: 'roofing', label: 'Roofing' },
];

const AVATAR_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'repair', label: 'Repair' },
  { value: 'speed', label: 'Speed' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'financing', label: 'Financing' },
  { value: 'new_homeowner', label: 'New Homeowner' },
  { value: 'energy_savings', label: 'Energy Savings' },
  { value: 'curb_appeal', label: 'Curb Appeal' },
  { value: 'storm_damage', label: 'Storm Damage' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'active_leak', label: 'Active Leak' },
  { value: 'preventative', label: 'Preventative' },
  { value: 'pets_kids', label: 'Pets & Kids' },
  { value: 'whole_home_refresh', label: 'Whole Home Refresh' },
];

const SOURCE_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'generated', label: 'Generated' },
  { value: 'uploaded', label: 'Uploaded' },
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'external_video_tool', label: 'External Video Tool' },
];

const ASPECT_RATIO_OPTIONS = [
  { value: '9:16', label: '9:16' },
  { value: '1:1', label: '1:1' },
  { value: '16:9', label: '16:9' },
];

const getResults = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const matchesClient = (recordClientId, selectedClientId) => {
  if (!selectedClientId) return true;
  if (!recordClientId) return true;
  return String(recordClientId) === String(selectedClientId);
};

const defaultHookTemplateForm = {
  client: null,
  name: '',
  vertical: 'bath',
  avatar: 'safety',
  pain_point: '',
  hook_text: '',
  bridge_text: '',
  overlay_text: '',
  voiceover_text: '',
  active: true,
};

const defaultBodyVideoForm = {
  client: null,
  name: '',
  vertical: 'bath',
  offer_type: '',
  aspect_ratio: '9:16',
  duration_seconds: '',
  active: true,
  file: null,
};

const defaultCTAForm = {
  client: null,
  name: '',
  cta_text: '',
  active: true,
  file: null,
};

const defaultHookAssetForm = {
  client: null,
  hook_template: null,
  name: '',
  source_type: 'uploaded',
  provider: '',
  active: true,
  file: null,
};

const defaultVariantForm = {
  client: null,
  name: '',
  vertical: 'bath',
  avatar: 'safety',
  hook_template: null,
  hook_asset: null,
  body_video_asset: null,
  cta_asset: null,
  voiceover_asset: null,
  notes: '',
};

const emptyDialog = { type: null, open: false };

function buildMultipartPayload(fields) {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false');
      return;
    }
    formData.append(key, value);
  });

  return formData;
}

function StatusChip({ value }) {
  if (!value) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  const colorMap = {
    draft: 'default',
    ready: 'info',
    rendering: 'warning',
    completed: 'success',
    failed: 'error',
    queued: 'info',
    processing: 'warning',
  };

  return <Chip size="small" label={String(value).replace(/_/g, ' ')} color={colorMap[value] || 'default'} />;
}

function CreativeStudioPage() {
  const message = useMessage();
  const [activeTab, setActiveTab] = useState('variants');
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialog, setDialog] = useState(emptyDialog);

  const [hookTemplates, setHookTemplates] = useState([]);
  const [hookAssets, setHookAssets] = useState([]);
  const [bodyVideoAssets, setBodyVideoAssets] = useState([]);
  const [ctaAssets, setCTAAssets] = useState([]);
  const [voiceoverAssets, setVoiceoverAssets] = useState([]);
  const [adVariants, setAdVariants] = useState([]);
  const [renderJobs, setRenderJobs] = useState([]);
  const [editingHookTemplateId, setEditingHookTemplateId] = useState(null);

  const [hookTemplateForm, setHookTemplateForm] = useState(defaultHookTemplateForm);
  const [bodyVideoForm, setBodyVideoForm] = useState(defaultBodyVideoForm);
  const [ctaForm, setCTAForm] = useState(defaultCTAForm);
  const [hookAssetForm, setHookAssetForm] = useState(defaultHookAssetForm);
  const [variantForm, setVariantForm] = useState(defaultVariantForm);
  const [submitting, setSubmitting] = useState(false);

  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientInputChange,
  } = useClientAutocomplete();

  const selectedClientId = selectedClient?.value || '';

  const filteredHookTemplates = useMemo(
    () => hookTemplates.filter((item) => matchesClient(item.client, selectedClientId)),
    [hookTemplates, selectedClientId]
  );
  const filteredHookAssets = useMemo(
    () => hookAssets.filter((item) => matchesClient(item.client, selectedClientId)),
    [hookAssets, selectedClientId]
  );
  const filteredBodyVideos = useMemo(
    () => bodyVideoAssets.filter((item) => String(item.client) === String(selectedClientId || item.client)),
    [bodyVideoAssets, selectedClientId]
  );
  const filteredCTAAssets = useMemo(
    () => ctaAssets.filter((item) => matchesClient(item.client, selectedClientId)),
    [ctaAssets, selectedClientId]
  );
  const filteredVoiceovers = useMemo(
    () => voiceoverAssets.filter((item) => matchesClient(item.client, selectedClientId)),
    [voiceoverAssets, selectedClientId]
  );
  const filteredVariants = useMemo(
    () => adVariants.filter((item) => String(item.client) === String(selectedClientId || item.client)),
    [adVariants, selectedClientId]
  );
  const filteredJobs = useMemo(
    () => renderJobs.filter((item) => String(item.client) === String(selectedClientId || item.client)),
    [renderJobs, selectedClientId]
  );

  useEffect(() => {
    let active = true;

    async function loadData() {
      setLoading(true);
      try {
        const [
          hookTemplatesResponse,
          hookAssetsResponse,
          bodyVideoResponse,
          ctaResponse,
          voiceoverResponse,
          variantsResponse,
          jobsResponse,
        ] = await Promise.all([
          creativeStudioApi.listHookTemplates(),
          creativeStudioApi.listHookAssets(),
          creativeStudioApi.listBodyVideoAssets(),
          creativeStudioApi.listCTAAssets(),
          creativeStudioApi.listVoiceoverAssets(),
          creativeStudioApi.listAdVariants(),
          creativeStudioApi.listRenderJobs(),
        ]);

        if (!active) return;

        setHookTemplates(getResults(hookTemplatesResponse?.data));
        setHookAssets(getResults(hookAssetsResponse?.data));
        setBodyVideoAssets(getResults(bodyVideoResponse?.data));
        setCTAAssets(getResults(ctaResponse?.data));
        setVoiceoverAssets(getResults(voiceoverResponse?.data));
        setAdVariants(getResults(variantsResponse?.data));
        setRenderJobs(getResults(jobsResponse?.data));
      } catch (error) {
        console.error('Failed to load creative studio data', error);
        if (active) {
          message.error('Unable to load Creative Studio data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [message, refreshKey]);

  const refreshAll = () => setRefreshKey((value) => value + 1);

  const openDialog = (type) => {
    setEditingHookTemplateId(null);
    if (type === 'hook-template') {
      setHookTemplateForm({
        ...defaultHookTemplateForm,
        client: selectedClient,
      });
    }
    if (type === 'body-video') {
      setBodyVideoForm({
        ...defaultBodyVideoForm,
        client: selectedClient,
      });
    }
    if (type === 'cta') {
      setCTAForm({
        ...defaultCTAForm,
        client: selectedClient,
      });
    }
    if (type === 'hook-asset') {
      setHookAssetForm({
        ...defaultHookAssetForm,
        client: selectedClient,
      });
    }
    if (type === 'variant') {
      setVariantForm({
        ...defaultVariantForm,
        client: selectedClient,
      });
    }
    setDialog({ type, open: true });
  };

  const editHookTemplate = (template) => {
    const matchedClient =
      clientOptions.find((option) => String(option.value) === String(template.client)) || null;
    setEditingHookTemplateId(template.id);
    setHookTemplateForm({
      client: matchedClient,
      name: template.name || '',
      vertical: template.vertical || defaultHookTemplateForm.vertical,
      avatar: template.avatar || defaultHookTemplateForm.avatar,
      pain_point: template.pain_point || '',
      hook_text: template.hook_text || '',
      bridge_text: template.bridge_text || '',
      overlay_text: template.overlay_text || '',
      voiceover_text: template.voiceover_text || '',
      active: template.active ?? true,
    });
    setDialog({ type: 'hook-template', open: true });
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialog(emptyDialog);
  };

  const handleRender = async (variantId) => {
    try {
      await creativeStudioApi.renderAdVariant(variantId);
      message.success('Render job queued.');
      refreshAll();
    } catch (error) {
      console.error('Failed to queue render', error);
      message.error(error?.response?.data?.detail || 'Unable to queue render job.');
    }
  };

  const submitHookTemplate = async () => {
    if (!hookTemplateForm.name.trim() || !hookTemplateForm.hook_text.trim() || !hookTemplateForm.bridge_text.trim()) {
      message.error('Name, hook text, and bridge text are required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        client: hookTemplateForm.client?.value || null,
        name: hookTemplateForm.name.trim(),
        vertical: hookTemplateForm.vertical,
        avatar: hookTemplateForm.avatar,
        pain_point: hookTemplateForm.pain_point.trim(),
        hook_text: hookTemplateForm.hook_text.trim(),
        bridge_text: hookTemplateForm.bridge_text.trim(),
        overlay_text: hookTemplateForm.overlay_text.trim(),
        voiceover_text: hookTemplateForm.voiceover_text.trim(),
        active: hookTemplateForm.active,
      };

      if (editingHookTemplateId) {
        await creativeStudioApi.updateHookTemplate(editingHookTemplateId, payload);
      } else {
        await creativeStudioApi.createHookTemplate(payload);
      }

      message.success(editingHookTemplateId ? 'Hook template updated.' : 'Hook template created.');
      setDialog(emptyDialog);
      setEditingHookTemplateId(null);
      refreshAll();
    } catch (error) {
      console.error('Failed to create hook template', error);
      message.error(editingHookTemplateId ? 'Unable to update hook template.' : 'Unable to create hook template.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitBodyVideo = async () => {
    if (!bodyVideoForm.client?.value || !bodyVideoForm.name.trim() || !bodyVideoForm.file) {
      message.error('Client, asset name, and file are required for a body video.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildMultipartPayload({
        client: bodyVideoForm.client.value,
        name: bodyVideoForm.name.trim(),
        vertical: bodyVideoForm.vertical,
        offer_type: bodyVideoForm.offer_type.trim(),
        aspect_ratio: bodyVideoForm.aspect_ratio,
        duration_seconds: bodyVideoForm.duration_seconds,
        active: bodyVideoForm.active,
        file: bodyVideoForm.file,
      });
      await creativeStudioApi.createBodyVideoAsset(payload);
      message.success('Body video asset created.');
      setDialog(emptyDialog);
      refreshAll();
    } catch (error) {
      console.error('Failed to create body video asset', error);
      message.error('Unable to create body video asset.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCTA = async () => {
    if (!ctaForm.name.trim() || !ctaForm.cta_text.trim()) {
      message.error('Name and CTA text are required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildMultipartPayload({
        client: ctaForm.client?.value || null,
        name: ctaForm.name.trim(),
        cta_text: ctaForm.cta_text.trim(),
        active: ctaForm.active,
        file: ctaForm.file,
      });
      await creativeStudioApi.createCTAAsset(payload);
      message.success('CTA asset created.');
      setDialog(emptyDialog);
      refreshAll();
    } catch (error) {
      console.error('Failed to create CTA asset', error);
      message.error('Unable to create CTA asset.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitHookAsset = async () => {
    if (!hookAssetForm.name.trim() || !hookAssetForm.file) {
      message.error('Name and file are required for a hook asset.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildMultipartPayload({
        client: hookAssetForm.client?.value || null,
        hook_template: hookAssetForm.hook_template?.id || null,
        name: hookAssetForm.name.trim(),
        source_type: hookAssetForm.source_type,
        provider: hookAssetForm.provider,
        active: hookAssetForm.active,
        file: hookAssetForm.file,
      });
      await creativeStudioApi.createHookAsset(payload);
      message.success('Hook asset created.');
      setDialog(emptyDialog);
      refreshAll();
    } catch (error) {
      console.error('Failed to create hook asset', error);
      message.error('Unable to create hook asset.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitVariant = async () => {
    if (!variantForm.client?.value || !variantForm.name.trim() || !variantForm.body_video_asset) {
      message.error('Client, variant name, and body video are required.');
      return;
    }

    if (!variantForm.hook_template && !variantForm.hook_asset) {
      message.error('Choose a hook template or a hook asset.');
      return;
    }

    setSubmitting(true);
    try {
      await creativeStudioApi.createAdVariant({
        client: variantForm.client.value,
        name: variantForm.name.trim(),
        vertical: variantForm.vertical,
        avatar: variantForm.avatar,
        hook_template: variantForm.hook_template?.id || null,
        hook_asset: variantForm.hook_asset?.id || null,
        body_video_asset: variantForm.body_video_asset.id,
        cta_asset: variantForm.cta_asset?.id || null,
        voiceover_asset: variantForm.voiceover_asset?.id || null,
        notes: variantForm.notes.trim(),
      });
      message.success('Ad variant created.');
      setDialog(emptyDialog);
      refreshAll();
    } catch (error) {
      console.error('Failed to create ad variant', error);
      message.error(error?.response?.data?.detail || 'Unable to create ad variant.');
    } finally {
      setSubmitting(false);
    }
  };

  const statCards = [
    {
      title: 'Hook Templates',
      value: filteredHookTemplates.length,
      subtitle: 'Reusable messaging blueprints',
    },
    {
      title: 'Body Videos',
      value: filteredBodyVideos.length,
      subtitle: 'Finished client body assets',
    },
    {
      title: 'Ad Variants',
      value: filteredVariants.length,
      subtitle: 'Modular ads ready for render',
    },
    {
      title: 'Queued / Processing',
      value: filteredJobs.filter((job) => ['queued', 'processing'].includes(job.status)).length,
      subtitle: 'Active render pipeline work',
    },
  ];

  return (
    <Page title="Creative Studio" fullWidth>
      <Stack spacing={3}>
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
              color: 'common.white',
              background:
                'linear-gradient(135deg, rgba(15,23,42,1) 0%, rgba(17,94,89,1) 45%, rgba(8,145,178,1) 100%)',
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: 1.2 }}>
                    Hook-First Ad Builder
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    Manage hooks, assets, modular variants, and render queue state.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={refreshAll}
                    sx={{ bgcolor: 'rgba(255,255,255,0.14)' }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<MovieCreationOutlinedIcon />}
                    onClick={() => openDialog('variant')}
                    sx={{ bgcolor: 'common.white', color: 'text.primary', '&:hover': { bgcolor: 'grey.100' } }}
                  >
                    New Ad Variant
                  </Button>
                </Stack>
              </Stack>

              <Autocomplete
                value={selectedClient}
                onChange={(event, value) => setSelectedClient(value)}
                onInputChange={handleClientInputChange}
                options={clientOptions}
                loading={clientsLoading}
                sx={{ maxWidth: 420 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by client"
                    placeholder="All clients"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </Box>
        </Card>

        <Grid container spacing={2}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} lg={3} key={card.title}>
              <Card sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="overline" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="h3">{card.value}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.subtitle}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={{ borderRadius: 3 }}>
          <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} variant="scrollable">
            {TAB_OPTIONS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
          <Divider />
          <CardContent>
            {loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            ) : null}

            {!loading && activeTab === 'hooks' ? (
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h6">Hook Strategy Library</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Store reusable messaging templates and optional concrete hook scene assets.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openDialog('hook-template')}>
                      New Hook Template
                    </Button>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog('hook-asset')}>
                      New Hook Asset
                    </Button>
                  </Stack>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} lg={7}>
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardHeader title="Hook Templates" />
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Vertical</TableCell>
                              <TableCell>Avatar</TableCell>
                              <TableCell>Hook</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredHookTemplates.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.client_name || 'Global'}</TableCell>
                                <TableCell>{item.vertical}</TableCell>
                                <TableCell>{item.avatar}</TableCell>
                                <TableCell sx={{ maxWidth: 360 }}>
                                  <Typography variant="body2" noWrap>{item.hook_text}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    variant="outlined"
                                    onClick={() => editHookTemplate(item)}
                                  >
                                    Edit
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!filteredHookTemplates.length ? (
                              <TableRow>
                                <TableCell colSpan={6}>
                                  <Typography variant="body2" color="text.secondary">No hook templates yet.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardHeader title="Hook Assets" />
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell>Provider</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredHookAssets.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.client_name || 'Global'}</TableCell>
                                <TableCell>{item.source_type}</TableCell>
                                <TableCell>{item.provider || '—'}</TableCell>
                              </TableRow>
                            ))}
                            {!filteredHookAssets.length ? (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography variant="body2" color="text.secondary">No hook assets yet.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            ) : null}

            {!loading && activeTab === 'assets' ? (
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h6">Asset Library</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage finished body videos, optional CTAs, and voiceover assets for future provider workflows.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<AddIcon />} variant="outlined" onClick={() => openDialog('cta')}>
                      New CTA
                    </Button>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog('body-video')}>
                      New Body Video
                    </Button>
                  </Stack>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} lg={7}>
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardHeader title="Body Video Assets" />
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Vertical</TableCell>
                              <TableCell>Aspect Ratio</TableCell>
                              <TableCell>Offer Type</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredBodyVideos.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.client_name}</TableCell>
                                <TableCell>{item.vertical}</TableCell>
                                <TableCell>{item.aspect_ratio}</TableCell>
                                <TableCell>{item.offer_type || '—'}</TableCell>
                              </TableRow>
                            ))}
                            {!filteredBodyVideos.length ? (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">No body videos yet.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <Stack spacing={2}>
                      <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardHeader title="CTA Assets" />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>CTA Text</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredCTAAssets.map((item) => (
                                <TableRow key={item.id} hover>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.client_name || 'Global'}</TableCell>
                                  <TableCell>{item.cta_text}</TableCell>
                                </TableRow>
                              ))}
                              {!filteredCTAAssets.length ? (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">No CTA assets yet.</Typography>
                                  </TableCell>
                                </TableRow>
                              ) : null}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Card>
                      <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardHeader title="Voiceover Assets" />
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Provider</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredVoiceovers.map((item) => (
                                <TableRow key={item.id} hover>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.client_name || 'Global'}</TableCell>
                                  <TableCell>{item.provider || '—'}</TableCell>
                                </TableRow>
                              ))}
                              {!filteredVoiceovers.length ? (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">No voiceovers yet.</Typography>
                                  </TableCell>
                                </TableRow>
                              ) : null}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Card>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            ) : null}

            {!loading && activeTab === 'variants' ? (
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                  <Box>
                    <Typography variant="h6">Ad Variant Builder</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create a modular ad by pairing hook strategy with a finished body video and optional CTA.
                    </Typography>
                  </Box>
                  <Button startIcon={<AutoAwesomeIcon />} variant="contained" onClick={() => openDialog('variant')}>
                    New Ad Variant
                  </Button>
                </Stack>
                <Alert severity="info">
                  This V1 UI creates and queues modular ad variants. The backend render task is scaffolded, but actual
                  FFmpeg composition still needs to be implemented.
                </Alert>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Vertical</TableCell>
                          <TableCell>Hook</TableCell>
                              <TableCell>Body Video</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Output</TableCell>
                              <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredVariants.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="subtitle2">{item.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{item.avatar}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{item.client_name}</TableCell>
                            <TableCell>{item.vertical}</TableCell>
                            <TableCell>{item.hook_asset_name || item.hook_template_name || '—'}</TableCell>
                            <TableCell>{item.body_video_asset_name || '—'}</TableCell>
                            <TableCell><StatusChip value={item.status} /></TableCell>
                            <TableCell>
                              {item.output_file ? (
                                <Button
                                  size="small"
                                  component="a"
                                  href={item.output_file}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open
                                </Button>
                              ) : '—'}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleRender(item.id)}
                                disabled={item.status === 'rendering'}
                              >
                                Render
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!filteredVariants.length ? (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <Typography variant="body2" color="text.secondary">No ad variants yet.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Stack>
            ) : null}

            {!loading && activeTab === 'jobs' ? (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6">Render Jobs</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Track queued, processing, completed, and failed job attempts for ad variants.
                  </Typography>
                </Box>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Ad Variant</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Started</TableCell>
                          <TableCell>Completed</TableCell>
                          <TableCell>Output</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredJobs.map((job) => (
                          <TableRow key={job.id} hover>
                            <TableCell>{job.id}</TableCell>
                            <TableCell>{job.ad_variant_name}</TableCell>
                            <TableCell><StatusChip value={job.status} /></TableCell>
                            <TableCell>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</TableCell>
                            <TableCell>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}</TableCell>
                            <TableCell>
                              {job.output_file ? (
                                <Button
                                  size="small"
                                  component="a"
                                  href={job.output_file}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Open
                                </Button>
                              ) : '—'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" noWrap>{job.error_log || '—'}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!filteredJobs.length ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography variant="body2" color="text.secondary">No render jobs yet.</Typography>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={dialog.open && dialog.type === 'hook-template'} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingHookTemplateId ? 'Edit Hook Template' : 'New Hook Template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              value={hookTemplateForm.client}
              onChange={(event, value) => setHookTemplateForm((prev) => ({ ...prev, client: value }))}
              onInputChange={handleClientInputChange}
              options={clientOptions}
              renderInput={(params) => <TextField {...params} label="Client (optional)" />}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Template name"
                  value={hookTemplateForm.name}
                  onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Vertical"
                  value={hookTemplateForm.vertical}
                  onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, vertical: event.target.value }))}
                >
                  {VERTICAL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Avatar"
                  value={hookTemplateForm.avatar}
                  onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, avatar: event.target.value }))}
                >
                  {AVATAR_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Pain point"
              value={hookTemplateForm.pain_point}
              onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, pain_point: event.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Hook text"
              value={hookTemplateForm.hook_text}
              onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, hook_text: event.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Bridge text"
              value={hookTemplateForm.bridge_text}
              onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, bridge_text: event.target.value }))}
            />
            <TextField
              fullWidth
              label="Overlay text"
              value={hookTemplateForm.overlay_text}
              onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, overlay_text: event.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Voiceover text"
              value={hookTemplateForm.voiceover_text}
              onChange={(event) => setHookTemplateForm((prev) => ({ ...prev, voiceover_text: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitHookTemplate} variant="contained" disabled={submitting}>
            {editingHookTemplateId ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog.open && dialog.type === 'body-video'} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>New Body Video Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              value={bodyVideoForm.client}
              onChange={(event, value) => setBodyVideoForm((prev) => ({ ...prev, client: value }))}
              onInputChange={handleClientInputChange}
              options={clientOptions}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Asset name"
                  value={bodyVideoForm.name}
                  onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Vertical"
                  value={bodyVideoForm.vertical}
                  onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, vertical: event.target.value }))}
                >
                  {VERTICAL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Aspect ratio"
                  value={bodyVideoForm.aspect_ratio}
                  onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, aspect_ratio: event.target.value }))}
                >
                  {ASPECT_RATIO_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Offer type"
                  value={bodyVideoForm.offer_type}
                  onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, offer_type: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Duration seconds"
                  value={bodyVideoForm.duration_seconds}
                  onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, duration_seconds: event.target.value }))}
                />
              </Grid>
            </Grid>
            <Button component="label" variant="outlined">
              {bodyVideoForm.file ? `Selected: ${bodyVideoForm.file.name}` : 'Select video file'}
              <input
                hidden
                type="file"
                accept="video/*"
                onChange={(event) => setBodyVideoForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitBodyVideo} variant="contained" disabled={submitting}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog.open && dialog.type === 'cta'} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>New CTA Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              value={ctaForm.client}
              onChange={(event, value) => setCTAForm((prev) => ({ ...prev, client: value }))}
              onInputChange={handleClientInputChange}
              options={clientOptions}
              renderInput={(params) => <TextField {...params} label="Client (optional)" />}
            />
            <TextField
              fullWidth
              label="Asset name"
              value={ctaForm.name}
              onChange={(event) => setCTAForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              fullWidth
              label="CTA text"
              value={ctaForm.cta_text}
              onChange={(event) => setCTAForm((prev) => ({ ...prev, cta_text: event.target.value }))}
            />
            <Button component="label" variant="outlined">
              {ctaForm.file ? `Selected: ${ctaForm.file.name}` : 'Select optional CTA file'}
              <input
                hidden
                type="file"
                accept="image/*,video/*"
                onChange={(event) => setCTAForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitCTA} variant="contained" disabled={submitting}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog.open && dialog.type === 'hook-asset'} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>New Hook Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              value={hookAssetForm.client}
              onChange={(event, value) => setHookAssetForm((prev) => ({ ...prev, client: value }))}
              onInputChange={handleClientInputChange}
              options={clientOptions}
              renderInput={(params) => <TextField {...params} label="Client (optional)" />}
            />
            <Autocomplete
              value={hookAssetForm.hook_template}
              onChange={(event, value) => setHookAssetForm((prev) => ({ ...prev, hook_template: value }))}
              options={filteredHookTemplates}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Hook template (optional)" />}
            />
            <TextField
              fullWidth
              label="Asset name"
              value={hookAssetForm.name}
              onChange={(event) => setHookAssetForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Source type"
                  value={hookAssetForm.source_type}
                  onChange={(event) => setHookAssetForm((prev) => ({ ...prev, source_type: event.target.value }))}
                >
                  {SOURCE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Provider"
                  value={hookAssetForm.provider}
                  onChange={(event) => setHookAssetForm((prev) => ({ ...prev, provider: event.target.value }))}
                >
                  {PROVIDER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Button component="label" variant="outlined">
              {hookAssetForm.file ? `Selected: ${hookAssetForm.file.name}` : 'Select hook file'}
              <input
                hidden
                type="file"
                accept="video/*"
                onChange={(event) => setHookAssetForm((prev) => ({ ...prev, file: event.target.files?.[0] || null }))}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitHookAsset} variant="contained" disabled={submitting}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog.open && dialog.type === 'variant'} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>New Ad Variant</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              value={variantForm.client}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, client: value }))}
              onInputChange={handleClientInputChange}
              options={clientOptions}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Variant name"
                  value={variantForm.name}
                  onChange={(event) => setVariantForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Vertical"
                  value={variantForm.vertical}
                  onChange={(event) => setVariantForm((prev) => ({ ...prev, vertical: event.target.value }))}
                >
                  {VERTICAL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Avatar"
                  value={variantForm.avatar}
                  onChange={(event) => setVariantForm((prev) => ({ ...prev, avatar: event.target.value }))}
                >
                  {AVATAR_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Autocomplete
              value={variantForm.hook_template}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, hook_template: value }))}
              options={filteredHookTemplates.filter(
                (item) => item.vertical === variantForm.vertical && item.avatar === variantForm.avatar
              )}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Hook template" />}
            />
            <Autocomplete
              value={variantForm.hook_asset}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, hook_asset: value }))}
              options={filteredHookAssets}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Hook asset (optional override)" />}
            />
            <Autocomplete
              value={variantForm.body_video_asset}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, body_video_asset: value }))}
              options={filteredBodyVideos.filter((item) => item.vertical === variantForm.vertical)}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Body video asset" />}
            />
            <Autocomplete
              value={variantForm.cta_asset}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, cta_asset: value }))}
              options={filteredCTAAssets}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="CTA asset (optional)" />}
            />
            <Autocomplete
              value={variantForm.voiceover_asset}
              onChange={(event, value) => setVariantForm((prev) => ({ ...prev, voiceover_asset: value }))}
              options={filteredVoiceovers}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => <TextField {...params} label="Voiceover asset (optional)" />}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes"
              value={variantForm.notes}
              onChange={(event) => setVariantForm((prev) => ({ ...prev, notes: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={submitVariant} variant="contained" disabled={submitting}>Create</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default CreativeStudioPage;
